/**
 * pages/SleepPage.js
 * 
 * Sleep tracking with:
 * - Log sleep sessions
 * - Duration bar chart (last 30 days)
 * - Quality distribution
 * - Analytics summary cards
 */

import React, { useState, useEffect, useCallback } from 'react';
import { sleepAPI } from '../utils/api';
import { format, parseISO } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import './SleepPage.css';

const QUALITY_LABELS = { 1: 'Terrible', 2: 'Poor', 3: 'Fair', 4: 'Good', 5: 'Excellent' };
const QUALITY_COLORS = { 1: '#ef4444', 2: '#f97316', 3: '#f59e0b', 4: '#10b981', 5: '#6366f1' };

const QualityStars = ({ quality, onChange }) => (
  <div className="quality-stars">
    {[1, 2, 3, 4, 5].map(n => (
      <button
        key={n}
        type="button"
        className={`star-btn ${quality >= n ? 'star-active' : ''}`}
        onClick={() => onChange(n)}
        style={{ color: quality >= n ? QUALITY_COLORS[quality] : 'var(--text-muted)' }}
        title={QUALITY_LABELS[n]}
      >
        ★
      </button>
    ))}
    {quality && <span className="quality-label">{QUALITY_LABELS[quality]}</span>}
  </div>
);

// Custom tooltip for recharts
const SleepTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const hours = (payload[0].value / 60).toFixed(1);
  return (
    <div className="chart-tooltip">
      <div className="tooltip-date">{label}</div>
      <div className="tooltip-val">{hours}h sleep</div>
    </div>
  );
};

function SleepLogModal({ onClose, onSave }) {
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  yesterday.setHours(22, 30, 0, 0);

  const [form, setForm] = useState({
    sleep_start: format(yesterday, "yyyy-MM-dd'T'HH:mm"),
    sleep_end: format(now, "yyyy-MM-dd'T'HH:mm"),
    quality: 3,
    notes: '',
    awakenings: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const update = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const durationMins = Math.max(0, (new Date(form.sleep_end) - new Date(form.sleep_start)) / 60000);
  const durationDisplay = durationMins > 0
    ? `${Math.floor(durationMins / 60)}h ${durationMins % 60}m`
    : 'Invalid times';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (new Date(form.sleep_end) <= new Date(form.sleep_start)) {
      return setError('Wake time must be after sleep time');
    }
    setLoading(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3>Log Sleep</h3>
          <button className="btn btn-ghost" onClick={onClose}>✕</button>
        </div>

        {error && <div className="error-message" style={{ marginBottom: '1rem' }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="sleep-form-grid">
            <div className="form-group">
              <label className="form-label">Bedtime</label>
              <input type="datetime-local" className="form-input" value={form.sleep_start} onChange={update('sleep_start')} required />
            </div>
            <div className="form-group">
              <label className="form-label">Wake Time</label>
              <input type="datetime-local" className="form-input" value={form.sleep_end} onChange={update('sleep_end')} required />
            </div>
          </div>

          {/* Duration preview */}
          <div className="duration-preview">
            <span>Duration: </span>
            <strong style={{ color: durationMins >= 420 ? 'var(--green)' : durationMins < 300 ? 'var(--red)' : 'var(--yellow)' }}>
              {durationDisplay}
            </strong>
          </div>

          <div className="form-group" style={{ marginBottom: '1rem' }}>
            <label className="form-label">Sleep Quality</label>
            <QualityStars quality={form.quality} onChange={(q) => setForm(f => ({ ...f, quality: q }))} />
          </div>

          <div className="sleep-form-grid" style={{ marginBottom: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Awakenings</label>
              <input type="number" className="form-input" value={form.awakenings} onChange={update('awakenings')} min="0" max="20" />
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: '1.5rem' }}>
            <label className="form-label">Notes</label>
            <textarea className="form-input" value={form.notes} onChange={update('notes')} placeholder="How did you sleep? Any factors?" rows={2} />
          </div>

          <div className="flex gap-sm" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <><div className="spinner" /> Saving...</> : 'Log Sleep'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function SleepPage() {
  const [logs, setLogs] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [days, setDays] = useState(30);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [logsData, analyticsData] = await Promise.all([
        sleepAPI.list({ days }),
        sleepAPI.analytics(days),
      ]);
      setLogs(logsData.logs);
      setAnalytics(analyticsData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async (data) => {
    await sleepAPI.create(data);
    loadData();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this sleep log?')) return;
    await sleepAPI.delete(id);
    loadData();
  };

  // Chart data
  const chartData = analytics?.trend?.map(d => ({
    date: format(parseISO(d.date), 'MMM d'),
    mins: d.duration_mins,
    quality: d.quality,
  })) || [];

  const avgHours = analytics?.averages?.avg_duration_mins
    ? (analytics.averages.avg_duration_mins / 60).toFixed(1)
    : '—';

  return (
    <div className="sleep-page animate-fadein">
      <div className="page-header">
        <div>
          <h1>Sleep Tracker</h1>
          <p className="text-muted">Monitor your rest and recovery</p>
        </div>
        <div className="flex gap-sm items-center">
          <select className="form-input" style={{ width: 'auto' }} value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ Log Sleep</button>
        </div>
      </div>

      {/* Analytics cards */}
      {analytics && (
        <div className="sleep-stats-grid">
          <div className="sleep-stat card">
            <div className="sleep-stat-value" style={{ color: 'var(--purple)' }}>{avgHours}h</div>
            <div className="sleep-stat-label">Avg Duration</div>
            <div className="sleep-stat-target">Target: 7-9h</div>
          </div>
          <div className="sleep-stat card">
            <div className="sleep-stat-value" style={{ color: 'var(--accent-light)' }}>
              {analytics.averages?.avg_quality || '—'}/5
            </div>
            <div className="sleep-stat-label">Avg Quality</div>
          </div>
          <div className="sleep-stat card">
            <div className="sleep-stat-value">{analytics.averages?.total_logs || 0}</div>
            <div className="sleep-stat-label">Nights Logged</div>
          </div>
          <div className="sleep-stat card">
            <div className="sleep-stat-value" style={{ color: 'var(--yellow)' }}>
              {analytics.averages?.avg_awakenings ?? '—'}
            </div>
            <div className="sleep-stat-label">Avg Awakenings</div>
          </div>
        </div>
      )}

      {/* Duration chart */}
      {chartData.length > 0 && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginBottom: '1.25rem' }}>Sleep Duration</h3>
          <div style={{ height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ left: -10 }}>
                <XAxis
                  dataKey="date"
                  tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => `${(v / 60).toFixed(0)}h`}
                  domain={[0, 'dataMax + 60']}
                />
                <Tooltip content={<SleepTooltip />} />
                {/* Reference line at 7h (420 mins) */}
                <Bar dataKey="mins" radius={[3, 3, 0, 0]} maxBarSize={30}>
                  {chartData.map((d, i) => (
                    <Cell
                      key={i}
                      fill={d.mins >= 420 ? 'var(--purple)' : d.mins < 300 ? 'var(--red)' : 'var(--yellow)'}
                      fillOpacity={0.85}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="chart-legend">
            <span style={{ color: 'var(--purple)' }}>● 7h+ (good)</span>
            <span style={{ color: 'var(--yellow)' }}>● 5-7h (fair)</span>
            <span style={{ color: 'var(--red)' }}>● &lt;5h (low)</span>
          </div>
        </div>
      )}

      {/* Sleep log list */}
      <div className="card">
        <h3 style={{ marginBottom: '1rem' }}>Sleep Log</h3>
        {loading ? (
          <div className="text-muted">Loading...</div>
        ) : logs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>◎</div>
            <p>No sleep logs yet. Start tracking your rest!</p>
          </div>
        ) : (
          <div className="sleep-log-list">
            {logs.map(log => {
              const hours = Math.floor(log.duration_mins / 60);
              const mins = log.duration_mins % 60;
              return (
                <div key={log.id} className="sleep-log-item">
                  <div className="sleep-log-date">
                    <div className="sleep-log-day">{format(parseISO(log.sleep_start), 'EEE')}</div>
                    <div className="sleep-log-num">{format(parseISO(log.sleep_start), 'd')}</div>
                    <div className="sleep-log-month">{format(parseISO(log.sleep_start), 'MMM')}</div>
                  </div>
                  <div className="sleep-log-main">
                    <div className="sleep-log-times">
                      <span>{format(parseISO(log.sleep_start), 'h:mm a')}</span>
                      <span className="arrow">→</span>
                      <span>{format(parseISO(log.sleep_end), 'h:mm a')}</span>
                    </div>
                    <div className="sleep-log-duration">
                      <span style={{ color: hours >= 7 ? 'var(--green)' : hours < 5 ? 'var(--red)' : 'var(--yellow)', fontFamily: 'var(--font-mono)' }}>
                        {hours}h {mins}m
                      </span>
                      {log.quality && (
                        <span style={{ color: QUALITY_COLORS[log.quality], marginLeft: '0.75rem' }}>
                          {'★'.repeat(log.quality)}{'☆'.repeat(5 - log.quality)}
                        </span>
                      )}
                      {log.awakenings > 0 && (
                        <span className="awakening-badge">{log.awakenings} awakenings</span>
                      )}
                    </div>
                    {log.notes && <div className="sleep-log-notes">{log.notes}</div>}
                  </div>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(log.id)}>✕</button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showModal && (
        <SleepLogModal
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
